/**
 * The translation editor's contract, narrowed from `unknown` at the boundary.
 *
 * The shape that matters is the **alignment target**: the server decides
 * which passages are translatable, and it excludes Qur'an (`passage_type <>
 * 'quran'`). The editor must not form its own opinion about that — a client
 * that guessed differently would post the wrong number of paragraphs and be
 * refused, or worse, attach every translation one passage out.
 */

export class ResponseShapeError extends Error {
  constructor(what: string) {
    super(`The server sent something this version cannot read: ${what}`);
    this.name = 'ResponseShapeError';
  }
}

export interface AlignmentTarget {
  readonly passageId: string;
  readonly ordinal: number;
  readonly passageType: string;
  readonly sourceText: string;
  /** The extractor was not confident about this passage's text. */
  readonly uncertain: boolean;
}

export interface EntryResult {
  readonly passagesTranslated: number;
  readonly awaitingCheck: number;
  readonly note: string;
}

export interface PublishResult {
  readonly translationsApproved: number;
  readonly publishedUnchecked: number;
  readonly note: string;
}

/** Who produced the translation. `publisher` is deliberately not offered. */
export type Provenance = 'human' | 'ai';

function asRecord(value: unknown, where: string): Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new ResponseShapeError(`${where} is not an object`);
  }
  return value as Record<string, unknown>;
}

export function parseAlignmentTargets(value: unknown): AlignmentTarget[] {
  if (!Array.isArray(value)) {
    throw new ResponseShapeError('the alignment response is not an array');
  }
  return value.map((entry, index) => {
    const target = asRecord(entry, `alignment[${index}]`);
    const passageId = target.passageId;
    const ordinal = target.ordinal;
    if (typeof passageId !== 'string' || typeof ordinal !== 'number') {
      throw new ResponseShapeError(`alignment[${index}] has no passage id or ordinal`);
    }
    return {
      passageId,
      ordinal,
      passageType: typeof target.passageType === 'string' ? target.passageType : 'body',
      sourceText: typeof target.sourceText === 'string' ? target.sourceText : '',
      uncertain: target.uncertain === true,
    };
  });
}

function num(source: Record<string, unknown>, key: string): number {
  const value = source[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

export function parseEntryResult(value: unknown): EntryResult {
  const source = asRecord(value, 'entry result');
  return {
    passagesTranslated: num(source, 'passagesTranslated'),
    awaitingCheck: num(source, 'awaitingCheck'),
    note: typeof source.note === 'string' ? source.note : '',
  };
}

export function parsePublishResult(value: unknown): PublishResult {
  const source = asRecord(value, 'publish result');
  return {
    translationsApproved: num(source, 'translationsApproved'),
    publishedUnchecked: num(source, 'publishedUnchecked'),
    note: typeof source.note === 'string' ? source.note : '',
  };
}

/**
 * Splits a pasted translation into paragraphs.
 *
 * Blank-line separated, because that is what a translator's document looks
 * like when it comes out of Word or a chat window. Trailing and leading blank
 * lines are dropped; **internal single newlines are kept**, since a passage
 * can legitimately be several lines of one paragraph and joining them would
 * silently reflow the translator's text.
 */
export function splitParagraphs(pasted: string): string[] {
  return pasted
    .replace(/\r\n/g, '\n')
    .split(/\n\s*\n+/)
    .map((paragraph) => paragraph.trim())
    .filter((paragraph) => paragraph.length > 0);
}

/**
 * What the counts mean before anything is sent.
 *
 * Checked in the browser so a mismatch is shown beside the paste box rather
 * than coming back as a 422 — but the server checks it too, and the server's
 * answer is the one that decides. This is a courtesy, not the guarantee.
 */
export function describeAlignment(
  pastedCount: number,
  expected: number,
): { ok: boolean; message: string } {
  if (pastedCount === expected) {
    return {
      ok: true,
      message: `${pastedCount} paragraphs — one for each translatable passage.`,
    };
  }
  if (pastedCount < expected) {
    return {
      ok: false,
      message:
        `${pastedCount} paragraphs pasted, but ${expected} passages need one. ` +
        'Two passages may have been merged into one paragraph.',
    };
  }
  return {
    ok: false,
    message:
      `${pastedCount} paragraphs pasted, but only ${expected} passages need one. ` +
      'A passage may have been split across two paragraphs, or the Qur’an ' +
      'passages were translated — those are never translated here.',
  };
}
